import React, { type FC } from "react";
import { AspectRatio, SimpleGrid } from "@chakra-ui/react";
import { type GetStaticProps } from "next";
import NextImage from "next/legacy/image";
import { trackEvent, trackMutinyConversion } from "lib/analytics";
import {
  type Page as SanityPage,
  type Banner,
  type News,
  getAnnouncementBanner,
  getPage,
  getNewsArticles,
} from "lib/sanity";
import { formatDate, getPageMetaImage } from "utils";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { ResourceCard } from "components/resource-card";
import { Button } from "ui/button";
import { SimpleTextHero } from "components/hero";
import Balancer from "react-wrap-balancer";
import { DownloadIcon } from "ui/icons";
import { hightouchLogosZip } from "../constants/brand-assets";

type PageProps = {
  news: News[];
  page: SanityPage;
  announcementBanner?: Banner;
};

const Press: FC<Readonly<PageProps>> = ({ news, page, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      footerOptions={{
        ctaProps: {
          title: <Balancer>Download our brand guide and press assets</Balancer>,
          description: "",
          actions: (
            <Button href={hightouchLogosZip} leftIcon={DownloadIcon}>
              Brand assets
            </Button>
          ),
        },
      }}
    >
      <SimpleTextHero
        title={page.title}
        description={page.description}
        actions={
          <Button
            href={hightouchLogosZip}
            leftIcon={DownloadIcon}
            onClick={() => {
              trackEvent("Brand Assets Downloaded", { page: "/press" });
              trackMutinyConversion("asset-downloaded");
            }}
          >
            Download logos
          </Button>
        }
      />

      <Section center my={0} pb={sectionSpacingY}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4 }} gap="small">
          {news.map((item) => (
            <ResourceCard
              key={item.link}
              href={item.link}
              eyebrow={formatDate(item.publishedAt)}
              title={item.title}
              image={
                <AspectRatio ratio={2 / 1} width="100%" display={["none", "block"]}>
                  <NextImage src={`${item.imageUrl}?w=700`} alt={item.title + "."} layout="fill" objectFit="cover" />
                </AspectRatio>
              }
              linkAriaLabel={`Read ${item.title}.`}
            />
          ))}
        </SimpleGrid>
      </Section>
    </Page>
  );
};

export default Press;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, news] = await Promise.all([
    getPage("/press"),
    getAnnouncementBanner(),
    getNewsArticles(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      news,
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};
